<script context="module">
  import { get } from "svelte/store";
  import { VIDEOS } from "../consts";
  import { switchVideos } from "@lib/client/video";
  import {
    activeVideo,
    conversationVideo,
    dwellVideo
  } from "@stores/global";

  /**
   * Return a randomly selected video from videos object
   *
   * @param    {object} videos videos collection object keyed by video name
   * @returns  {object}        Single video object
   */
  export function shuffleVideos(videos) {
    if (Array.isArray(videos)) {
      return videos.sort(() => Math.random() - 0.5)[0];
    }

    return Object.values(videos).sort(() => Math.random() - 0.5)[0];
  }

  /**
   * Toggle the currently active video and switch
   * <video> elements in DOM
   *
   * @returns {object} chainable playback functions
   */
  export function toggleActiveVideo() {
    let oldVideo;
    let newVideo;

    if (get(activeVideo) === VIDEOS.DWELL) {
      activeVideo.update(() => VIDEOS.CONVERSATION);
      oldVideo = get(dwellVideo);
      newVideo = get(conversationVideo);
    } else {
      activeVideo.update(() => VIDEOS.DWELL);
      oldVideo = get(conversationVideo);
      newVideo = get(dwellVideo);
    }

    return switchVideos(newVideo, oldVideo);
  }
</script>

<script>
  import { onMount } from "svelte";
  import { supportsEnhancedExperience } from "@utils/support";
  import { cueVideo } from "@lib/client/video";
  import { maximized, response } from "@stores/global";
  import { dwellLoaded } from "@stores/videos";
  export let active = false;
  export let element;
  export let muted = true;
  export let src;
  export let transparent = false;
  export let type;

  let controls = true;
  let mounted = false;

  onMount(() => {
    controls = !supportsEnhancedExperience;
    mounted = true;
  });

  $: {
    if (element && mounted) {
      const playing = !element.paused && !element.ended && element.readyState > 2;

      if (type === VIDEOS.DWELL || !playing) {
        const cued = cueVideo(element, type, src);

        if (type === VIDEOS.DWELL) {
          cued.andPlay(() => {
            dwellLoaded.set(true);
          });
        }
      }
    }
  }

  // eslint-disable-next-line no-undefined
  $: transcript = $response ? $response.transcript : undefined;
  // eslint-disable-next-line no-undefined
  $: describedby = active && transcript ? "transcript" : undefined;

  // eslint-disable-next-line no-undefined
  const videoSrc = supportsEnhancedExperience ? undefined : src;
</script>

<style>
  video {
    height: 100%;
    left: 50%;
    object-fit: contain;
    opacity: 0;
    position: absolute;
    top: 0;
    transform: translate3d(-50%, 0, 0);
    transition: opacity 0.5s ease-out;
    width: auto;
    z-index: 1;
  }

  .active {
    opacity: 1;
    z-index: 2;
  }

  .transparent {
    opacity: 0;
  }
</style>

<video
  aria-describedby={describedby}
  bind:this={element}
  class:active
  class:maximized={$maximized}
  class:transparent
  data-video-type={type}
  {controls}
  {muted}
  playsinline
  preload="metadata"
  src={videoSrc} />
